import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api';
import { LangSwitcher } from '../components/LangSwitcher';
import { useTurnstile } from '../turnstile';

const ERROR_KEY: Record<string, string> = {
  captcha_failed: 'forgot.captchaFailed',
  rate_limited: 'forgot.rateLimited',
};

/**
 * Phase 6 step 1. Email in, neutral confirmation out.
 *
 * The success screen is shown for every accepted request, including addresses
 * with no account — matching the server, which returns the same 200 either
 * way. Reporting "no such account" here would hand back exactly the
 * enumeration the endpoint is built to refuse.
 */
export function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const captcha = useTurnstile();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.forgotPassword(email, captcha.token ?? undefined);
      setSent(true);
    } catch (err) {
      const key = err instanceof ApiError ? ERROR_KEY[err.message] : undefined;
      setError(t(key ?? 'forgot.failed'));
      // Single-use tokens: the widget must be re-challenged before a retry,
      // or the second attempt fails as captcha rather than as itself.
      captcha.reset();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      {sent ? (
        <div className="space-y-4">
          <h1 className="text-xl font-semibold text-slate-900">{t('forgot.sentTitle')}</h1>
          <p className="text-sm text-slate-600">{t('forgot.sent')}</p>
          <Link to="/login" className="block text-sm text-sky-600 hover:text-sky-700">
            {t('forgot.backToLogin')}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <h1 className="text-xl font-semibold text-slate-900">{t('forgot.title')}</h1>
          <p className="text-sm text-slate-600">{t('forgot.intro')}</p>

          <div className="space-y-1">
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              {t('forgot.email')}
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="username"
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>

          <div ref={captcha.ref} className="empty:hidden" />

          {error && (
            <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-md p-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-sky-600 px-4 py-2 text-white font-medium hover:bg-sky-700 disabled:opacity-50"
          >
            {busy ? t('forgot.sending') : t('forgot.submit')}
          </button>

          <Link to="/login" className="block text-sm text-sky-600 hover:text-sky-700">
            {t('forgot.backToLogin')}
          </Link>
        </form>
      )}
    </Shell>
  );
}

/** The signed-out card, shared with the reset page. */
export function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="flex justify-end p-4">
        <LangSwitcher />
      </header>
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
